import React, { useState, useEffect } from "react";
import {
  Container,
  Modal,
  Form,
  Spinner,
} from "react-bootstrap";
import CommonLayout from "../../../layouts/CommonLayout";
import { useNavigate } from "react-router-dom";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { toast } from "react-toastify";
import { extractObjectId } from "../../../utils/utils";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {
  FaTags,
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaStar,
  FaMusic,
  FaSmile,
  FaHashtag,
  FaClock,
  FaCog,
  FaTimes,
  FaExclamationTriangle
} from "react-icons/fa";
import "./TagsPage.css";

const TAG_TYPES = [
  { value: 'genre', label: 'Genre', icon: FaMusic },
  { value: 'mood', label: 'Mood', icon: FaSmile },
  { value: 'topic', label: 'Topic', icon: FaHashtag },
  { value: 'era', label: 'Era', icon: FaClock },
  { value: 'custom', label: 'Custom', icon: FaCog },
];

function TagsPage() {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tagToDelete, setTagToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    type: 'custom',
    description: '',
    priority: 0,
    isActive: true,
    isFeatured: false,
    color: '#6366f1',
  });

  const navigate = useNavigate();

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (filterType) params.type = filterType;
      if (searchQuery) params.search = searchQuery;

      const response = await allAPiServicesCall.getTags(params, getAuthConfig(), navigate);
      if (response?.data?.tags) {
        const sortedTags = [...response.data.tags].sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        setTags(sortedTags);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast.error("Failed to fetch tags");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [filterType, searchQuery]);

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    try {
      const response = await allAPiServicesCall.createTag(formData, getAuthConfig(), navigate);
      if (response?.success) {
        toast.success("Tag created successfully");
        setShowAddModal(false);
        resetForm();
        fetchTags();
      }
    } catch (error) {
      console.error("Error creating tag:", error);
      toast.error(error?.response?.data?.message || "Failed to create tag");
    }
  };

  const handleEditTag = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, tagId: extractObjectId(editingTag._id) };
      const response = await allAPiServicesCall.updateTag(payload, getAuthConfig(), navigate);
      if (response?.success) {
        toast.success("Tag updated successfully");
        setShowEditModal(false);
        setEditingTag(null);
        resetForm();
        fetchTags();
      }
    } catch (error) {
      console.error("Error updating tag:", error);
      toast.error(error?.response?.data?.message || "Failed to update tag");
    }
  };

  const confirmDelete = (tag) => {
    setTagToDelete(tag);
    setShowDeleteModal(true);
  };

  const handleDeleteTag = async () => {
    if (!tagToDelete) return;
    try {
      const response = await allAPiServicesCall.deleteTag(extractObjectId(tagToDelete._id), getAuthConfig(), navigate);
      if (response?.success) {
        toast.success("Tag deleted successfully");
        setShowDeleteModal(false);
        setTagToDelete(null);
        fetchTags();
      }
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast.error("Failed to delete tag");
    }
  };

  const openEditModal = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      type: tag.type,
      description: tag.description || '',
      priority: tag.priority || 0,
      isActive: tag.isActive !== false,
      isFeatured: tag.isFeatured || false,
      color: tag.color || '#6366f1',
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'custom',
      description: '',
      priority: 0,
      isActive: true,
      isFeatured: false,
      color: '#6366f1',
    });
  };

  // Calculate stats
  const stats = {
    total: tags.length,
    genre: tags.filter(t => t.type === 'genre').length,
    mood: tags.filter(t => t.type === 'mood').length,
    topic: tags.filter(t => t.type === 'topic').length,
    featured: tags.filter(t => t.isFeatured).length,
  };

  const renderTagForm = (isEdit = false) => (
    <>
      <div className="tags-form-row">
        <div className="tags-form-group">
          <label className="tags-form-label required">Tag Name</label>
          <input
            type="text"
            name="name"
            className="tags-form-input"
            value={formData.name}
            onChange={handleFormChange}
            required
            placeholder="e.g., Hip Hop, Summer Vibes"
          />
        </div>
        <div className="tags-form-group">
          <label className="tags-form-label">Type</label>
          <select
            name="type"
            className="tags-form-select"
            value={formData.type}
            onChange={handleFormChange}
          >
            {TAG_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="tags-form-group">
        <label className="tags-form-label">Description</label>
        <textarea
          name="description"
          className="tags-form-textarea"
          value={formData.description}
          onChange={handleFormChange}
          placeholder="Brief description of this tag"
          rows={2}
        />
      </div>

      <div className="tags-form-row-3">
        <div className="tags-form-group">
          <label className="tags-form-label">Priority</label>
          <input
            type="number"
            name="priority"
            className="tags-form-input"
            value={formData.priority}
            onChange={handleFormChange}
            min={0}
          />
          <span className="tags-form-hint">Lower numbers appear first</span>
        </div>
        <div className="tags-form-group">
          <label className="tags-form-label">Color</label>
          <input
            type="color"
            name="color"
            className="tags-color-input"
            value={formData.color}
            onChange={handleFormChange}
          />
        </div>
        <div className="tags-form-group">
          <label className="tags-form-label">Options</label>
          <div className="tags-switches">
            <div className="tags-switch-item">
              <Form.Check
                type="switch"
                id={isEdit ? "editIsActive" : "isActive"}
                name="isActive"
                checked={formData.isActive}
                onChange={handleFormChange}
              />
              <label htmlFor={isEdit ? "editIsActive" : "isActive"} className="form-check-label">Active</label>
            </div>
            <div className="tags-switch-item">
              <Form.Check
                type="switch"
                id={isEdit ? "editIsFeatured" : "isFeatured"}
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleFormChange}
              />
              <label htmlFor={isEdit ? "editIsFeatured" : "isFeatured"} className="form-check-label">Featured</label>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <CommonLayout>
      <div className="tags-page">
        <Container className="tags-container mt-5 pt-5">
          {/* Premium Header */}
          <div className="tags-header">
            <div className="tags-header-content">
              <div className="tags-header-left">
                <div className="tags-header-icon">
                  <FaTags />
                </div>
                <div className="tags-header-text">
                  <h1>Tags Management</h1>
                  <p>Organize and categorize your content with custom tags</p>
                </div>
              </div>
              <button className="tags-add-btn" onClick={() => setShowAddModal(true)}>
                <FaPlus />
                Add New Tag
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="tags-stats-grid">
            <div className="tags-stat-card">
              <div className="tags-stat-icon total">
                <FaTags />
              </div>
              <div className="tags-stat-info">
                <div className="tags-stat-value">{stats.total}</div>
                <div className="tags-stat-label">Total Tags</div>
              </div>
            </div>
            <div className="tags-stat-card">
              <div className="tags-stat-icon genre">
                <FaMusic />
              </div>
              <div className="tags-stat-info">
                <div className="tags-stat-value">{stats.genre}</div>
                <div className="tags-stat-label">Genre Tags</div>
              </div>
            </div>
            <div className="tags-stat-card">
              <div className="tags-stat-icon mood">
                <FaSmile />
              </div>
              <div className="tags-stat-info">
                <div className="tags-stat-value">{stats.mood}</div>
                <div className="tags-stat-label">Mood Tags</div>
              </div>
            </div>
            <div className="tags-stat-card">
              <div className="tags-stat-icon topic">
                <FaHashtag />
              </div>
              <div className="tags-stat-info">
                <div className="tags-stat-value">{stats.topic}</div>
                <div className="tags-stat-label">Topic Tags</div>
              </div>
            </div>
            <div className="tags-stat-card">
              <div className="tags-stat-icon featured">
                <FaStar />
              </div>
              <div className="tags-stat-info">
                <div className="tags-stat-value">{stats.featured}</div>
                <div className="tags-stat-label">Featured</div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="tags-filters-card">
            <div className="tags-filters-grid">
              <div className="tags-filter-group">
                <label className="tags-filter-label">Filter by Type</label>
                <select
                  className="tags-filter-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="">All Types</option>
                  {TAG_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="tags-filter-group">
                <label className="tags-filter-label">Search Tags</label>
                <div className="tags-search-wrapper">
                  <FaSearch />
                  <input
                    type="text"
                    className="tags-filter-input with-icon"
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tags Table */}
          <div className="tags-table-card">
            <div className="tags-table-header">
              <div className="tags-table-title">
                <FaTags />
                All Tags
                <span className="tags-table-count">{tags.length}</span>
              </div>
            </div>

            {isLoading ? (
              <div className="tags-loading">
                <Spinner animation="border" />
                <span>Loading tags...</span>
              </div>
            ) : tags.length === 0 ? (
              <div className="tags-empty-state">
                <div className="tags-empty-icon">
                  <FaTags />
                </div>
                <h4 className="tags-empty-title">No Tags Found</h4>
                <p className="tags-empty-text">Create your first tag to get started organizing your content.</p>
                <button className="tags-empty-btn" onClick={() => setShowAddModal(true)}>
                  <FaPlus /> Create First Tag
                </button>
              </div>
            ) : (
              <table className="tags-table">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Name</th>
                    <th>Slug</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Featured</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tags.map((tag) => (
                    <tr key={tag._id}>
                      <td>
                        <span className="tag-priority">{tag.priority}</span>
                      </td>
                      <td>
                        <div className="tag-name-cell">
                          {tag.color && (
                            <span className="tag-color-dot" style={{ backgroundColor: tag.color }} />
                          )}
                          <div className="tag-name-info">
                            <span className="tag-name-text">{tag.name}</span>
                            {tag.description && (
                              <span className="tag-description">{tag.description}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="tag-slug">{tag.slug}</span>
                      </td>
                      <td>
                        <span className={`tag-type-badge ${tag.type}`}>{tag.type}</span>
                      </td>
                      <td>
                        <span className={`tag-status-badge ${tag.isActive ? 'active' : 'inactive'}`}>
                          {tag.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        {tag.isFeatured && (
                          <span className="tag-featured-badge">
                            <FaStar /> Featured
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="tag-actions">
                          <button
                            className="tag-action-btn edit"
                            onClick={() => openEditModal(tag)}
                          >
                            <FaEdit /> Edit
                          </button>
                          <button
                            className="tag-action-btn delete"
                            onClick={() => confirmDelete(tag)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Container>

        {/* Add Tag Modal */}
        <Modal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          size="lg"
          centered
          className="tags-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaPlus /> Add New Tag
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleAddTag}>
            <Modal.Body>
              {renderTagForm(false)}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="tags-modal-btn secondary"
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="tags-modal-btn primary">
                <FaPlus /> Create Tag
              </button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Edit Tag Modal */}
        <Modal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          size="lg"
          centered
          className="tags-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <FaEdit /> Edit Tag
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleEditTag}>
            <Modal.Body>
              {renderTagForm(true)}
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                className="tags-modal-btn secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button type="submit" className="tags-modal-btn primary">
                Update Tag
              </button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          show={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(false);
            setTagToDelete(null);
          }}
          onConfirm={handleDeleteTag}
          title="Delete Tag"
          message={`Are you sure you want to delete the tag "${tagToDelete?.name}"? This action cannot be undone.`}
          confirmText="Delete Tag"
          cancelText="Cancel"
          variant="danger"
          icon={<FaTrash />}
        />
      </div>
    </CommonLayout>
  );
}

export default TagsPage;
